//#region Imports
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { GenericMemberResponsePortalResponseDto } from '@app/core/models/genericMembeResponsePortalResponseDto';
import { Helper } from '@rawlings/core';
import { MemberResponseHelper } from '@app/member-response-portal/shared/member-response-helper';
import { NotificationRequestService } from '@app/member-response-portal/service/notification-request.service';
import { NotifierService } from '@app/core/services/notifier-service';
import { Observable, catchError, map, of } from 'rxjs';
import { SharedMemberResponseService } from '@app/member-response-portal/service/shared-member-response.service';
import { SocialSecurityBenefitsDto } from './../../model/socialSecurityBenefitsDto';
import { SocialSecurityBenefitsService } from './../../service/social-security-benefits.service';
import { NOT_APPLICABLE, SURVEYSTEPS_INDEX } from '@app/member-response-portal/model/constants';
import { SurveyStepBase } from '@app/member-response-portal/shared/survey-step-base';
//#endregion Imports
@Component({
  selector: 'app-survey-social-security-benefits',
  templateUrl: './survey-social-security-benefits.component.html',
  styleUrls: ['./survey-social-security-benefits.component.scss'],
})
export class SurveySocialSecurityBenefitsComponent extends SurveyStepBase implements OnInit
{
  //#region Public Variables
  public ShowDateOfApplication: boolean;
  public ShowIsSupposedReceivingSocialSecurityBenefits: boolean;
  public ShowSpouseDateOfApplication: boolean;
  public StepIndex: number = SURVEYSTEPS_INDEX.SOCIAL_SECURITY_BENEFIT;
  //#endregion Public Variables

  //#region Protected Variables
  //#endregion Protected Variables

  //#region Private Variables
  //#endregion Private Variables

  //#region Properties
  public get HasReceivedSocialSecurityBenefits(): FormControl {
    return this.Form.get('HasReceivedSocialSecurityBenefits') as FormControl;
  }

  public get IsSpouseReceivingSocialSecurityBenefits(): FormControl {
    return this.Form.get(
      'IsSpouseReceivingSocialSecurityBenefits'
    ) as FormControl;
  }

  public get DateOfApplication(): FormControl {
    return this.Form.get('DateOfApplication') as FormControl;
  }

  public get SpouseDateOfApplication(): FormControl {
    return this.Form.get('SpouseDateOfApplication') as FormControl;
  }

  private get SocialSecurityBenefitsDto(): SocialSecurityBenefitsDto {
    const socialSecurityBenefitsDto: SocialSecurityBenefitsDto = {
      QueueLetterId: this.sharedMemberResponseService.GetQueueLetterId(),
      SocialSecurityBenefitsId: this.Form.get('SocialSecurityBenefitsId').value,
      SurveyResponseId: this.Form.get('SurveyResponseId').value,
      HasReceivedSocialSecurityBenefits:
        MemberResponseHelper.GetBoolFromYesNoValue(
          this.Form.get('HasReceivedSocialSecurityBenefits').value
        ),
      DateOfApplication: MemberResponseHelper.getDateWithZeroTime(
        this.Form.get('DateOfApplication')?.value
      ),
      IsSpouseReceivingSocialSecurityBenefits:
        MemberResponseHelper.GetBoolFromYesNoValue(
          this.Form.get('IsSpouseReceivingSocialSecurityBenefits').value
        ),
      SpouseDateOfApplication: MemberResponseHelper.getDateWithZeroTime(
        this.Form.get('SpouseDateOfApplication')?.value
      ),
      IsCompleted: this.Form.valid,
    };
    return socialSecurityBenefitsDto;
  }
  //#endregion Properties

  //#region Constructor
  constructor(
    protected notifierService: NotifierService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected sharedMemberResponseService: SharedMemberResponseService,
    private fb: FormBuilder,
    private notificationRequestService: NotificationRequestService,
    private socialSecurityBenefitsService: SocialSecurityBenefitsService
  ) {
    super(
        `Social Security Benefits`,
        SURVEYSTEPS_INDEX.OTHER_HEALTH_INSURANCE,
        false,
        'SocialSecurityBenefits',
        changeDetectorRef,
        notifierService,
        sharedMemberResponseService
      );
  }

  public ngOnInit(): void {
    this.InitStep();

    this.HasReceivedSocialSecurityBenefits.valueChanges
      .pipe()
      .subscribe((value: string) => {
        if (!Helper.IsEmpty(value)) {
          this.ShowDateOfApplication =
            MemberResponseHelper.GetBoolFromYesNoValue(value);
          this.IsSpouseReceivingSocialSecurityBenefits.addValidators(
            Validators.required
          );

          if (this.ShowDateOfApplication) {
            this.DateOfApplication.addValidators(Validators.required);
          } else {
            this.DateOfApplication.setValue(null);
            this.DateOfApplication.removeValidators(Validators.required);
          }

          this.DateOfApplication.updateValueAndValidity();
          this.IsSpouseReceivingSocialSecurityBenefits.updateValueAndValidity();

          this.ShowIsSupposedReceivingSocialSecurityBenefits = true;
        }
      });
  }
  //#endregion Constructor

  //#region Public Methods
  public CreateSocialSecurityBenefitFormGroup(): FormGroup {
    return this.fb.group({
      SurveyResponseId: [this.SurveyResponseId, Validators.required],
      SocialSecurityBenefitsId: [0, Validators.required],
      HasReceivedSocialSecurityBenefits: ['', Validators.required],
      DateOfApplication: [''],
      IsSpouseReceivingSocialSecurityBenefits: [''],
      SpouseDateOfApplication: [''],
      IsCompleted: [''],
    });
  }

  public HandleSpouseToggleEvent(event: {
    IsChecked: boolean;
    IsConfirmed: boolean;
  }): void {
    if (
      !Helper.IsBoolean(event.IsChecked) &&
      Helper.IsEmpty(event.IsChecked) &&
      event.IsConfirmed
    ) {
      this.ShowSpouseDateOfApplication = false;
    } else {
      this.ShowSpouseDateOfApplication = event.IsChecked;
    }
    this.SetValidation_Spouse(this.ShowSpouseDateOfApplication);
  }

  //#endregion Public Methods

  //#region Protected Methods
  protected BuildForm(): void {
    this.Form = this.CreateSocialSecurityBenefitFormGroup();
  }

  protected GetFormData() : any {
    return this.SocialSecurityBenefitsDto;
  }

  protected HandleRequest(): Observable<boolean> {
    return this.socialSecurityBenefitsService
      .Save(this.SocialSecurityBenefitsDto)
      .pipe(
        map(
          (
            result: GenericMemberResponsePortalResponseDto<SocialSecurityBenefitsDto>
          ): boolean => {
            if (!result.Success) {
                result.IsSurveySubmitted ? this.notificationRequestService.ShowFailedRequestAndLogout(result) : this.notificationRequestService.ShowFailedRequestAndLogout(null);
              return false;
            }
            return true;
          }
        ),
        catchError(() => {
          return of(false);
        })
      );
  }

  protected LoadData(): void {
    const ininitialData: any = this.sharedMemberResponseService.GetSurveySummarySection(this.FormKey);

    if(ininitialData){
        this.SetDataToForm(ininitialData);
        this.SetValidation_Spouse(this.ShowSpouseDateOfApplication);
    }
  }
  //#endregion Protected Methods

  //#region Private Methods
  private SetDataToForm(
    socialSecurityBenefitsDto: SocialSecurityBenefitsDto
  ): void {
    this.Form.patchValue({
      SurveyResponseId: socialSecurityBenefitsDto.SurveyResponseId,
      SocialSecurityBenefitsId:
        socialSecurityBenefitsDto.SocialSecurityBenefitsId,
      HasReceivedSocialSecurityBenefits:
        MemberResponseHelper.GetStringToggleValue(
          socialSecurityBenefitsDto.HasReceivedSocialSecurityBenefits
        ),
      DateOfApplication: socialSecurityBenefitsDto.DateOfApplication,
      IsSpouseReceivingSocialSecurityBenefits:
        MemberResponseHelper.GetStringToggleValue(
          socialSecurityBenefitsDto.IsSpouseReceivingSocialSecurityBenefits
        ) ?? NOT_APPLICABLE,
      SpouseDateOfApplication:
        socialSecurityBenefitsDto.SpouseDateOfApplication,
    });

    this.ShowSpouseDateOfApplication = MemberResponseHelper.GetBooleanToggleValue(socialSecurityBenefitsDto.IsSpouseReceivingSocialSecurityBenefits);

    this.Form.updateValueAndValidity();
  }

  private SetValidation_Spouse(value: boolean): void {
    this.ShowSpouseDateOfApplication = value;
    if (this.ShowSpouseDateOfApplication) {
      this.SpouseDateOfApplication.addValidators(Validators.required);
    } else {
      this.ShowSpouseDateOfApplication = false;
      this.SpouseDateOfApplication.setValue(null);
      this.SpouseDateOfApplication.removeValidators(Validators.required);
    }
    this.SpouseDateOfApplication.updateValueAndValidity();
  }
  //#region Private Methods
}
